import { useState } from 'react';

import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import Typography from '@mui/material/Typography';
import DialogTitle from '@mui/material/DialogTitle';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';

import { useTranslate } from 'src/locales';
import { useGetPresentationPackQuery } from 'src/services/presentations/presentations.service';

import { PackRow } from '../modals/shortlist-pack-dialog';

interface Props {
  packId: string;
  /** Called when the recruiter opens it, so the notification can be marked read. */
  onOpened?: VoidFunction;
}

/**
 * "Open" on the notification that a shortlist pack, comparison or bulk run is ready (JT1-1887). The
 * file is downloaded from here, after confirming the final check's findings when it found any,
 * wherever the recruiter started it (a bulk run from the candidate list has no job to list it on).
 */
export default function PackNotificationAction({ packId, onOpened }: Props) {
  const { t } = useTranslate();
  const [open, setOpen] = useState(false);

  return (
    <>
      <Stack direction="row" spacing={1} sx={{ mt: 1.5 }}>
        <Button size="small" variant="contained" onClick={() => { setOpen(true); onOpened?.(); }}>
          {t('presentations.packs.open')}
        </Button>
      </Stack>
      {open && <PackDialog packId={packId} onClose={() => setOpen(false)} />}
    </>
  );
}

function PackDialog({ packId, onClose }: { packId: string; onClose: VoidFunction }) {
  const { t } = useTranslate();
  const { currentData: pack, isError } = useGetPresentationPackQuery(packId);

  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{pack?.title ?? t('presentations.packs.title')}</DialogTitle>
      <DialogContent>
        {isError && <Typography variant="body2" sx={{ color: 'text.secondary' }}>{t('presentations.packs.not_found')}</Typography>}
        {pack && <PackRow pack={pack} />}
      </DialogContent>
      <DialogActions>
        <Button color="inherit" onClick={onClose}>{t('common.close')}</Button>
      </DialogActions>
    </Dialog>
  );
}
